import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ContactService, SenderContact, ContactInput } from '../../services/contact.service';
import { useUserStore } from '../../store/user.store';

// ─── Tag color map ─────────────────────────────────────────────────────────
const TAG_COLORS: Record<string, string> = {
    '客户': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    '仓库': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    '分支': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
    '合作方': 'bg-green-500/15 text-green-400 border-green-500/30',
};

const TAG_OPTIONS = ['客户', '仓库', '分支', '合作方'];

// ─── BLANK FORM ─────────────────────────────────────────────────────────────
const BLANK: ContactInput = {
    name: '',
    phone: '',
    address: '',
    city: '',
    note: '',
    tag: '',
    is_default: false,
};

// ─── ContactForm modal ───────────────────────────────────────────────────────
interface ContactFormProps {
    initial?: SenderContact | null;
    onClose: () => void;
    onSubmit: (input: ContactInput) => Promise<void>;
}

const ContactForm: React.FC<ContactFormProps> = ({ initial, onClose, onSubmit }) => {
    const [form, setForm] = useState<ContactInput>(initial ? {
        name: initial.name,
        phone: initial.phone || '',
        address: initial.address || '',
        city: initial.city || '',
        note: initial.note || '',
        tag: initial.tag || '',
        is_default: initial.is_default,
    } : BLANK);
    const [saving, setSaving] = useState(false);

    const set = (k: keyof ContactInput, v: unknown) =>
        setForm(prev => ({ ...prev, [k]: v }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error('收货人姓名不能为空');
            return;
        }
        setSaving(true);
        try {
            await onSubmit(form);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[90] flex items-end justify-center" onClick={onClose}>
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <form
                onClick={e => e.stopPropagation()}
                onSubmit={handleSubmit}
                className="relative w-full max-w-lg bg-[#0a1628] rounded-t-3xl border-t border-white/10 animate-slide-up flex flex-col"
                style={{ maxHeight: '92vh' }}
            >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
                    <div className="w-10 h-1 rounded-full bg-white/20" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 flex-shrink-0 border-b border-white/5">
                    <h2 className="text-base font-bold text-white">
                        {initial ? '编辑收货人' : '新增收货人'}
                    </h2>
                    <button type="button" onClick={onClose}
                        className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                        <span className="material-icons text-xl">close</span>
                    </button>
                </div>

                {/* Scrollable fields */}
                <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-4 flex flex-col gap-4">

                    {/* 姓名 */}
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
                            收货人姓名 <span className="text-red-500">*</span>
                        </label>
                        <input
                            autoFocus
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                            placeholder="请输入收货人全名"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 text-sm outline-none focus:border-primary/50 focus:bg-primary/5 transition-colors"
                        />
                    </div>

                    {/* 电话 + 城市 */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">电话</label>
                            <input
                                type="tel"
                                value={form.phone}
                                onChange={e => set('phone', e.target.value)}
                                placeholder="手机号"
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 text-sm outline-none focus:border-primary/50 focus:bg-primary/5 transition-colors"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">城市</label>
                            <input
                                value={form.city}
                                onChange={e => set('city', e.target.value)}
                                placeholder="城市/区域"
                                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 text-sm outline-none focus:border-primary/50 focus:bg-primary/5 transition-colors"
                            />
                        </div>
                    </div>

                    {/* 地址 */}
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">详细地址</label>
                        <input
                            value={form.address}
                            onChange={e => set('address', e.target.value)}
                            placeholder="街道、楼层、门牌号等"
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 text-sm outline-none focus:border-primary/50 focus:bg-primary/5 transition-colors"
                        />
                    </div>

                    {/* 标签 */}
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-2">标签</label>
                        <div className="flex gap-2 flex-wrap">
                            {TAG_OPTIONS.map(t => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => set('tag', form.tag === t ? '' : t)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${form.tag === t
                                            ? (TAG_COLORS[t] || 'bg-primary/20 text-primary border-primary/40')
                                            : 'bg-white/5 text-gray-500 border-white/10 hover:border-white/20'
                                        }`}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* 备注 */}
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">备注</label>
                        <textarea
                            value={form.note}
                            onChange={e => set('note', e.target.value)}
                            placeholder="可选备注信息..."
                            rows={2}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-600 text-sm outline-none focus:border-primary/50 focus:bg-primary/5 transition-colors resize-none"
                        />
                    </div>

                    {/* 设为默认 */}
                    <label className="flex items-center gap-3 p-3 bg-white/3 rounded-xl cursor-pointer hover:bg-white/5 transition-colors">
                        <div className="relative">
                            <input
                                type="checkbox"
                                checked={!!form.is_default}
                                onChange={e => set('is_default', e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-10 h-6 rounded-full bg-white/10 peer-checked:bg-primary transition-colors" />
                            <div className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                        </div>
                        <span className="text-sm text-gray-300">设为默认收货人</span>
                    </label>
                </div>

                {/* Submit */}
                <div className="px-5 py-4 flex-shrink-0 border-t border-white/5">
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full py-3.5 rounded-2xl bg-primary text-white font-bold text-sm tracking-wide hover:bg-primary/80 active:scale-[0.98] disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                    >
                        {saving
                            ? <><span className="material-icons text-base animate-spin">refresh</span> 保存中...</>
                            : <><span className="material-icons text-base">save</span> 保存</>}
                    </button>
                </div>
            </form>
        </div>
    );
};

// ─── Contact card ────────────────────────────────────────────────────────────
interface CardProps {
    contact: SenderContact;
    onEdit: () => void;
    onDelete: () => void;
    onSetDefault: () => void;
}

const ContactCard: React.FC<CardProps> = ({ contact, onEdit, onDelete, onSetDefault }) => {
    const tagColor = TAG_COLORS[contact.tag || ''] || '';

    return (
        <div className={`bg-[#0f2040] rounded-2xl border p-4 flex flex-col gap-2 transition-all ${contact.is_default ? 'border-primary/40 shadow-lg shadow-primary/5' : 'border-white/5'
            }`}>
            {/* Top row */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm ${contact.is_default ? 'bg-primary/20 text-primary' : 'bg-white/5 text-gray-400'
                        }`}>
                        {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-white font-semibold text-sm">{contact.name}</span>
                            {contact.is_default && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-primary/20 text-primary font-bold border border-primary/30">
                                    默认
                                </span>
                            )}
                            {contact.tag && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-md border font-medium ${tagColor || 'bg-white/5 text-gray-500 border-white/10'}`}>
                                    {contact.tag}
                                </span>
                            )}
                        </div>
                        {contact.phone && (
                            <div className="text-xs text-gray-500 mt-0.5 font-mono">{contact.phone}</div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1 flex-shrink-0">
                    {!contact.is_default && (
                        <button
                            onClick={onSetDefault}
                            title="设为默认"
                            className="p-1.5 rounded-lg text-gray-600 hover:text-amber-400 hover:bg-amber-400/10 transition-colors"
                        >
                            <span className="material-icons text-base">star_border</span>
                        </button>
                    )}
                    <button
                        onClick={onEdit}
                        className="p-1.5 rounded-lg text-gray-600 hover:text-primary hover:bg-primary/10 transition-colors"
                    >
                        <span className="material-icons text-base">edit</span>
                    </button>
                    <button
                        onClick={onDelete}
                        className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                    >
                        <span className="material-icons text-base">delete_outline</span>
                    </button>
                </div>
            </div>

            {/* Address */}
            {(contact.city || contact.address) && (
                <div className="flex items-start gap-1.5 text-xs text-gray-500 mt-0.5">
                    <span className="material-icons text-gray-700" style={{ fontSize: '14px', marginTop: '1px' }}>place</span>
                    <span className="leading-relaxed">
                        {[contact.city, contact.address].filter(Boolean).join(' · ')}
                    </span>
                </div>
            )}

            {/* Note */}
            {contact.note && (
                <div className="text-[11px] text-gray-600 italic pl-1 pt-0.5 border-t border-white/5">
                    {contact.note}
                </div>
            )}
        </div>
    );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const ContactsManager: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useUserStore();
    const [contacts, setContacts] = useState<SenderContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [editTarget, setEditTarget] = useState<SenderContact | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<SenderContact | null>(null);
    const [deleting, setDeleting] = useState(false);

    const companyId: string = (user as any)?.company_id ?? '';

    const loadContacts = useCallback(async () => {
        setLoading(true);
        try {
            setContacts(await ContactService.list());
        } catch (e: any) {
            toast.error('加载失败：' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadContacts(); }, [loadContacts]);

    const filtered = contacts.filter(c =>
        !search || c.name.includes(search) || c.phone?.includes(search) ||
        c.address?.includes(search) || c.city?.includes(search)
    );

    // ── 新增
    const handleCreate = async (input: ContactInput) => {
        await ContactService.create(input, companyId);
        toast.success('收货人已添加 ✓');
        setShowForm(false);
        await loadContacts();
    };

    // ── 编辑
    const handleUpdate = async (input: ContactInput) => {
        await ContactService.update(editTarget!.id, input, companyId);
        toast.success('已更新 ✓');
        setEditTarget(null);
        setShowForm(false);
        await loadContacts();
    };

    // ── 删除确认
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await ContactService.remove(deleteTarget.id);
            toast.success('已删除');
            setDeleteTarget(null);
            await loadContacts();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setDeleting(false);
        }
    };

    // ── 设为默认
    const handleSetDefault = async (contact: SenderContact) => {
        await ContactService.setDefault(contact.id, companyId);
        toast.success(`${contact.name} 已设为默认收货人 ✓`);
        await loadContacts();
    };

    return (
        <div className="min-h-screen bg-background-dark flex flex-col">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-surface-dark/95 backdrop-blur border-b border-white/5 shadow-md">
                <div className="px-4 py-4 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                    >
                        <span className="material-icons">arrow_back</span>
                    </button>
                    <div className="flex-1">
                        <h1 className="text-lg font-bold text-white">收货人管理</h1>
                        <div className="text-xs text-gray-500">
                            {contacts.length > 0 ? `共 ${contacts.length} 位联系人` : '尚无联系人'}
                        </div>
                    </div>
                    <button
                        onClick={() => { setEditTarget(null); setShowForm(true); }}
                        className="flex items-center gap-1 px-3 py-2 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/80 active:scale-95 transition-all"
                    >
                        <span className="material-icons text-base">add</span>
                        新增
                    </button>
                </div>

                {/* Search */}
                <div className="px-4 pb-3">
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                        <span className="material-icons text-gray-600 text-lg">search</span>
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="搜索姓名、电话、地址..."
                            className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none"
                        />
                        {search && (
                            <button onClick={() => setSearch('')} className="text-gray-600 hover:text-gray-400">
                                <span className="material-icons text-base">close</span>
                            </button>
                        )}
                    </div>
                </div>
            </header>

            {/* Body */}
            <main className="flex-1 px-4 py-4 flex flex-col gap-3 pb-28 overflow-y-auto no-scrollbar">
                {loading ? (
                    <div className="flex flex-col items-center py-20 text-gray-600">
                        <span className="material-icons text-4xl animate-spin mb-3">refresh</span>
                        <span className="text-sm">加载中...</span>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center py-20 text-gray-600">
                        <span className="material-icons text-5xl mb-4 text-gray-700">contacts</span>
                        {search
                            ? <><div className="text-sm font-medium text-gray-400 mb-1">没有匹配的联系人</div>
                                <div className="text-xs text-gray-600">尝试搜索其他关键词</div></>
                            : <><div className="text-sm font-medium text-gray-400 mb-1">还没有收货人</div>
                                <div className="text-xs text-gray-600 mb-6">点击右上角「新增」添加第一位收货人</div>
                                <button
                                    onClick={() => { setEditTarget(null); setShowForm(true); }}
                                    className="px-5 py-2.5 rounded-xl bg-primary/15 text-primary border border-primary/30 text-sm font-bold hover:bg-primary/25 transition-colors"
                                >
                                    + 新增收货人
                                </button></>}
                    </div>
                ) : (
                    filtered.map(c => (
                        <ContactCard
                            key={c.id}
                            contact={c}
                            onEdit={() => { setEditTarget(c); setShowForm(true); }}
                            onDelete={() => setDeleteTarget(c)}
                            onSetDefault={() => handleSetDefault(c)}
                        />
                    ))
                )}
            </main>

            {/* Add / Edit Form */}
            {showForm && (
                <ContactForm
                    initial={editTarget}
                    onClose={() => { setShowForm(false); setEditTarget(null); }}
                    onSubmit={editTarget ? handleUpdate : handleCreate}
                />
            )}

            {/* Delete confirmation sheet */}
            {deleteTarget && (
                <div className="fixed inset-0 z-[90] flex items-end justify-center" onClick={() => setDeleteTarget(null)}>
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                    <div
                        className="relative w-full max-w-lg bg-[#0a1628] rounded-t-3xl border-t border-white/10 animate-slide-up px-5 py-6"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-center mb-5">
                            <div className="w-12 h-12 rounded-2xl bg-red-500/15 flex items-center justify-center">
                                <span className="material-icons text-red-400 text-2xl">delete_forever</span>
                            </div>
                        </div>
                        <div className="text-center mb-6">
                            <div className="text-white font-bold text-base mb-1">确认删除？</div>
                            <div className="text-sm text-gray-500">
                                将删除「<span className="text-white">{deleteTarget.name}</span>」，此操作不可撤销。
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="flex-1 py-3 rounded-2xl bg-white/5 text-gray-400 font-bold text-sm border border-white/10"
                            >
                                取消
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="flex-1 py-3 rounded-2xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 active:scale-[0.98] disabled:opacity-60 transition-all flex items-center justify-center gap-1.5"
                            >
                                {deleting
                                    ? <><span className="material-icons text-sm animate-spin">refresh</span> 删除中</>
                                    : '确认删除'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsManager;
